import { useCallback, useState } from 'react'
import { CreateTxOptions } from '@terra-money/terra.js'
import {
  CreateTxFailed,
  Timeout,
  TxFailed,
  TxResult,
  TxUnspecifiedError,
  useConnectedWallet,
  UserDenied,
} from '@terra-money/wallet-provider'
import { TxStatus } from 'interfaces/tx.interface'
import { useModal } from 'react-modal-hook'
import TxModal from 'components/Modal/TxModal'
import { baseLcdService } from 'services/base/baseLcdService'
import CapIsFully from 'components/Modal/CapIsFully'
import { AmplitudeEvent, AmplitudeUtils } from 'utils/AmplitudeUtils'

const useBroadcast = () => {
  const [txResult, setTxResult] = useState<TxResult>()
  const [txError, setTxError] = useState<string>()
  const [txStatus, setTxStatus] = useState<TxStatus>()
  const { lcdClient } = baseLcdService

  const connectedWallet = useConnectedWallet()

  const [showTxModal, hideTxModal] = useModal(
    () => (
      <TxModal
        onClose={hideTxModal}
        txStatus={txStatus}
        txError={txError}
        txResult={txResult}
      />
    ),
    [txStatus, txError, txResult],
  )

  const [showCapIsFull, hideCapIsFull] = useModal(() => (
    <CapIsFully onClose={hideCapIsFull} />
  ))

  const send = useCallback(
    (
      txs: CreateTxOptions,
      message = 'Sending transaction',
      callback?: Function,
    ) => {
      if (!connectedWallet) {
        return
      }

      setTxStatus(TxStatus.PENDING_APPROVE)
      showTxModal()

      connectedWallet
        .post(txs)
        .then((nextTxResult: TxResult) => {
          setTxResult(nextTxResult)
          setTxStatus(TxStatus.PENDING_RECEIPT)
          const txHash = nextTxResult.result.txhash
          AmplitudeUtils.logEvent(AmplitudeEvent.BroadcastTx, {
            message,
            txs,
            txHash,
          })
          const intervalId = setInterval(async () => {
            let status = ''

            const txInfo = await lcdClient.tx.txInfo(txHash)
            if (txInfo.logs) {
              status = 'success'
            } else if (txInfo.raw_log?.includes('failed')) {
              status = 'reverted'
            }

            if (status === 'success') {
              if (callback) {
                await callback()
              }
              setTxStatus(TxStatus.SUCCESS)
              clearInterval(intervalId)
            } else if (status === 'reverted') {
              setTxStatus(TxStatus.REVERT)
              clearInterval(intervalId)
            }
          }, 3000)
        })
        .catch((error: unknown) => {
          if (error instanceof UserDenied) {
            setTxError('User rejected transaction')
          } else if (error instanceof CreateTxFailed) {
            setTxError('Create Tx Failed: ' + error.message)
          } else if (error instanceof TxFailed) {
            setTxError('Tx Failed: ' + error.message)
          } else if (error instanceof Timeout) {
            setTxError('Timeout')
          } else if (error instanceof TxUnspecifiedError) {
            setTxError('Unspecified Error: ' + error.message)
          } else {
            setTxError(
              'Unknown Error: ' +
                (error instanceof Error ? error.message : String(error)),
            )
          }
          setTxStatus(TxStatus.FAILED)
          if ((error as any).message.includes('Amount is exceeding cap')) {
            hideTxModal()
            showCapIsFull()
          }
          AmplitudeUtils.logEvent(AmplitudeEvent.BroadcastError, {
            message,
            txs,
            error,
          })
          console.error(`Error on sending transaction : ${error}`)
        })
    },
    [connectedWallet, hideTxModal, lcdClient.tx, showCapIsFull, showTxModal],
  )

  return { send, txResult, txError, txStatus }
}

export default useBroadcast
